import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../authContext";
import styles from "../styles/MfaForm.module.css";

const CODE_LENGTH = 6;

// Mismo criterio que las cards: el backend manda un `code` estable y aca se
// traduce, para no depender del texto ni del idioma del error de Cognito.
const ERROR_MESSAGES = {
    INVALID_CODE: "El código no es correcto. Revisa el correo e inténtalo de nuevo",
    INVALID_CODE_FORMAT: "El código tiene 6 dígitos",
    EXPIRED_SESSION: "El código expiró. Vuelve a iniciar sesión para recibir uno nuevo",
    MFA_LOCKED: "Demasiados intentos. Espera unos minutos antes de reintentar",
    COGNITO_ERROR: "No pudimos verificar el código. Inténtalo de nuevo",
};

export default function MfaForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setTokens } = useAuth();

    // Llega por el state de navigate, no por cookie: la Session es una
    // credencial temporal y no tiene por que quedar accesible desde JS.
    const { session, email, destination } = location.state || {};

    const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputsRef = useRef([]);

    // Igual que en el panel: el estado `loading` no frena dos eventos del mismo
    // tick, porque ambos leen el valor previo al re-render. Aquí importa más,
    // porque cada envío consume uno de los cinco intentos que tolera Cognito.
    const inFlight = useRef(false);

    const code = digits.join('');

    // Entrar a /mfa directo por URL no tiene sentido: sin session no hay
    // challenge que responder.
    useEffect(() => {
        if (!session || !email) {
            navigate('/login', { replace: true });
        }
    }, [session, email, navigate]);

    useEffect(() => {
        inputsRef.current[0]?.focus();
    }, []);

    const writeDigits = (next, focusIndex) => {
        setDigits(next);
        setError(null);
        if (focusIndex !== undefined) {
            const target = Math.max(0, Math.min(CODE_LENGTH - 1, focusIndex));
            inputsRef.current[target]?.focus();
        }
    };

    const handleChange = (index, value) => {
        const clean = value.replace(/\D/g, '');
        if (!clean) return;

        // Si el gestor de contraseñas o el teclado del móvil vuelca varios
        // dígitos de golpe, se reparten desde esta casilla en adelante.
        const next = [...digits];
        clean.split('').forEach((digit, offset) => {
            if (index + offset < CODE_LENGTH) next[index + offset] = digit;
        });
        writeDigits(next, index + clean.length);
    };

    const handleKeyDown = (index, event) => {
        if (event.key === 'Backspace') {
            event.preventDefault();
            const next = [...digits];
            // Borrar una casilla vacía retrocede, que es lo que espera quien
            // mantiene pulsado el borrado.
            if (next[index]) {
                next[index] = '';
                writeDigits(next, index);
            } else {
                next[Math.max(0, index - 1)] = '';
                writeDigits(next, index - 1);
            }
        } else if (event.key === 'ArrowLeft') {
            inputsRef.current[index - 1]?.focus();
        } else if (event.key === 'ArrowRight') {
            inputsRef.current[index + 1]?.focus();
        } else if (event.key === 'Enter' && code.length === CODE_LENGTH) {
            handleSubmit();
        }
    };

    const handlePaste = (event) => {
        const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (!pasted) return;
        event.preventDefault();
        const next = Array(CODE_LENGTH).fill('');
        pasted.split('').forEach((digit, i) => { next[i] = digit; });
        writeDigits(next, pasted.length);
    };

    async function handleSubmit() {
        // Enter llama directo a este handler, sin pasar por el botón que se
        // bloquea al cargar.
        if (inFlight.current) return;

        if (code.length !== CODE_LENGTH) {
            setError(ERROR_MESSAGES.INVALID_CODE_FORMAT);
            return;
        }

        inFlight.current = true;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                process.env.REACT_APP_BACKEND_HOST + '/auth/respondToAuthChallenge',
                {
                    challengeName: 'EMAIL_OTP',
                    challengeResponses: {
                        USERNAME: email,
                        EMAIL_OTP_CODE: code,
                    },
                    session,
                }
            );

            setTokens(response.data);
            navigate('/');
        } catch (err) {
            const errorCode = err.response?.data?.code;

            // Con la sesion vencida no sirve reintentar aca: hay que pedir un
            // codigo nuevo desde el login.
            if (errorCode === 'EXPIRED_SESSION') {
                navigate('/login', {
                    replace: true,
                    state: { message: ERROR_MESSAGES.EXPIRED_SESSION },
                });
                return;
            }

            setError(ERROR_MESSAGES[errorCode] || "No pudimos verificar el código. Inténtalo de nuevo");
            setDigits(Array(CODE_LENGTH).fill(''));
            inputsRef.current[0]?.focus();
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }

    return (
        <div>
            <p className={styles.hint}>
                Enviamos un código a
                <span className={styles.destination}>{destination || 'tu correo'}</span>
            </p>

            <div
                className={styles.codeRow}
                onPaste={handlePaste}
                role="group"
                aria-label="Código de verificación de 6 dígitos"
            >
                {digits.map((digit, index) => (
                    <input
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        ref={(el) => { inputsRef.current[index] = el; }}
                        className={`${styles.digit} ${error ? styles.digitError : ''}`}
                        value={digit}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onFocus={(e) => e.target.select()}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        maxLength={CODE_LENGTH}
                        aria-label={`Dígito ${index + 1}`}
                        disabled={loading}
                    />
                ))}
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            <button
                onClick={handleSubmit}
                disabled={loading || code.length !== CODE_LENGTH}
                className={styles.submitBtn}
            >
                {loading ? "Verificando..." : "Verificar"}
            </button>

            {/* Reenviar implica un login nuevo, y la contrasena no se guarda en
                memoria a proposito, asi que se vuelve al formulario. */}
            <button
                onClick={() => navigate('/login', { replace: true })}
                disabled={loading}
                className={styles.secondaryBtn}
            >
                Volver a iniciar sesión
            </button>

            <p className={styles.footnote}>
                El código expira en unos minutos. Si no lo ves, revisa la carpeta de spam.
            </p>
        </div>
    );
}
